import { Form, Input, Modal, Space, Spin, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { buildIdNameMaps, type PortfolioProjectRow } from '@/api/portfolio/api';
import type { ResolveSharePointSharingLinkResponseDTO } from '@/api/projects/api';
import { appRoutes, navigateTo } from '@/app/routes';
import { SharePointLinkResolver } from '@/components/common/SharePointLinkResolver';
import {
  isDirectorOrHigher,
  isProductOrProjectManager,
} from '@/components/pages/portfolio/portfolio_table.utils';
import { PortfolioFilters } from '@/components/pages/portfolio/PortfolioFilters';
import { PortfolioStatCards } from '@/components/pages/portfolio/PortfolioStatCards';
import { PortfolioTable } from '@/components/pages/portfolio/PortfolioTable';
import { useAuthContext } from '@/hooks/auth.store';
import { useDevRoleOverride } from '@/hooks/useDevRoleOverride';
import { usePortfolio } from '@/hooks/usePortfolio';
import { useProjectSharePointEdit } from '@/hooks/useProjectSharePointEdit';

const { Paragraph, Text, Title } = Typography;

interface ClientProjectsPageProps {
  clientId: string;
  clientNameById: Record<string, string>;
  onClientNamesResolved?: (names: Record<string, string>) => void;
  onProjectNamesResolved?: (names: Record<string, string>) => void;
}

const ClientProjectsPage: React.FC<ClientProjectsPageProps> = ({
  clientId,
  clientNameById,
  onClientNamesResolved,
  onProjectNamesResolved,
}) => {
  const { accessToken, microsoftOboAssertion, user } = useAuthContext();
  const { roleState } = useDevRoleOverride();
  const directorOrHigher = useMemo(() => isDirectorOrHigher(user), [user]);
  const hasMetricAccess = useMemo(() => {
    if (roleState) {
      const isDirector = roleState.level === 'director' || roleState.level === 'vp';
      const isPM =
        roleState.role === 'pm' ||
        roleState.role === 'project_management' ||
        roleState.role === 'client_management';
      return isDirector || isPM;
    }
    return isDirectorOrHigher(user) || isProductOrProjectManager(user);
  }, [roleState, user]);

  const portfolio = usePortfolio({ accessToken, isDirectorOrHigher: directorOrHigher });
  const { setClientFilter } = portfolio;

  // The portfolio filter matches rows by client_name (value === label by design — see
  // buildFilterOptionsFromRows). The URL carries the HubSpot client_id, so we resolve
  // the display name before filtering. clientNameById is the fast path (populated when
  // navigating from the portfolio page); portfolio.allRows is the fallback for direct
  // URL loads / refreshes.
  const resolvedClientName = useMemo(
    () =>
      clientNameById[clientId] ??
      portfolio.allRows.find((r) => r.client_id === clientId)?.client_name,
    [clientId, clientNameById, portfolio.allRows],
  );

  // Feeds this page's already-fetched portfolio rows into the shared App-level name cache, so a
  // cold deep link straight into this route (before Portfolio has ever loaded) self-heals the
  // breadcrumb without an extra network call.
  useEffect(() => {
    if (portfolio.allRows.length === 0) {
      return;
    }
    const { clientNameById: resolvedClients, projectNameById } = buildIdNameMaps(portfolio.allRows);
    onClientNamesResolved?.(resolvedClients);
    onProjectNamesResolved?.(projectNameById);
  }, [portfolio.allRows, onClientNamesResolved, onProjectNamesResolved]);

  // Guard: setClientFilter dispatches SET_FILTER which resets page to 1, and its
  // useCallback identity changes on every call (project_id.filter() returns a new []
  // reference each time). Without this ref, the effect would re-fire on every render,
  // resetting page to 1 and making pagination appear broken.
  const appliedClientNameRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!resolvedClientName) {
      return;
    }
    if (appliedClientNameRef.current === resolvedClientName) {
      return;
    }
    appliedClientNameRef.current = resolvedClientName;
    setClientFilter([resolvedClientName]);
  }, [resolvedClientName, setClientFilter]);

  const {
    isEditModalOpen,
    editingRow,
    isEditLoading,
    isEditSubmitting,
    editProjectForm,
    messageContext,
    openEditProject,
    closeEditModal,
    handleSharePointResolved,
    handleEditProjectSubmit,
  } = useProjectSharePointEdit();

  const handleRowClick = useCallback((row: PortfolioProjectRow) => {
    navigateTo(appRoutes.projectHome(row.client_id, row.id));
  }, []);

  const clientName = resolvedClientName ?? clientId;
  const isInitializing = portfolio.loading || !portfolio.filters.client_id.length;

  return (
    <>
      {messageContext}

      <Space direction="vertical" size="large" className="page-stack pf-page">
        <div className="pf-page__header">
          <div className="pf-page__heading">
            <Title level={3} className="pf-page__title">
              {clientName}
            </Title>
            <Text type="secondary" className="pf-page__subtitle">
              {`${portfolio.total} active project${portfolio.total === 1 ? '' : 's'}`}
            </Text>
          </div>
        </div>

        {hasMetricAccess && !isInitializing && (
          <PortfolioStatCards
            stats={portfolio.stats}
            activePreset={portfolio.status}
            onSelect={portfolio.applyStatusPreset}
          />
        )}

        {isInitializing ? (
          <div className="pf-filters pf-filters--loading">
            <Spin size="small" />
            <Text type="secondary">Loading filters…</Text>
          </div>
        ) : (
          <PortfolioFilters
            options={portfolio.filterOptions}
            filters={portfolio.filters}
            hideClientFilter
            onClientChange={portfolio.setClientFilter}
            onProjectChange={portfolio.setProjectFilter}
            onOfferingChange={portfolio.setOfferingFilter}
            onLeaderChange={portfolio.setLeaderFilter}
            onSowStatusChange={portfolio.setSowStatus}
            onHealthChange={portfolio.setHealthFilter}
            onSearchChange={portfolio.setProjectSearch}
            onDateRangeChange={portfolio.setDateRange}
            onClearAll={portfolio.clearAllFilters}
          />
        )}

        {!isInitializing && (
          <PortfolioTable
            rows={portfolio.rows}
            total={portfolio.total}
            page={portfolio.page}
            perPage={portfolio.perPage}
            loading={portfolio.loading}
            error={portfolio.error}
            sortBy={portfolio.sortBy}
            sortDir={portfolio.sortDir}
            onChangePage={portfolio.changePage}
            onChangeSort={portfolio.setSort}
            onRetry={portfolio.retry}
            onRowClick={handleRowClick}
            onClientClick={() => undefined}
            onAddSharePointLink={openEditProject}
          />
        )}

        <div className="pf-page__footer">
          <span className="pf-page__legend">
            <span className="pf-missing-badge" aria-hidden="true">
              ⚠
            </span>
            <Text type="secondary">Missing = weekly update older than 7 days</Text>
          </span>
          <Text type="secondary" className="pf-page__source">
            <span className="pf-page__source-dot" aria-hidden="true" />
            Source: HubSpot · OpenAir · Snowflake
          </Text>
        </div>
      </Space>

      <Modal
        title={`Edit Project — ${editingRow ? editingRow.project_name : ''}`}
        open={isEditModalOpen}
        onCancel={closeEditModal}
        onOk={() => void handleEditProjectSubmit()}
        okText="Save Changes"
        confirmLoading={isEditSubmitting}
        width={760}
        destroyOnHidden
      >
        {isEditLoading ? (
          <Text type="secondary">Loading project…</Text>
        ) : (
          <Form form={editProjectForm} layout="vertical">
            <Title level={5}>Microsoft 365 (SharePoint folder)</Title>
            <Paragraph type="secondary" style={{ marginTop: 0 }}>
              Paste a sharing link and choose Resolve. The API calls Microsoft Graph using app
              credentials (see infrastructure docs). Save to persist the folder binding.
            </Paragraph>
            <Form.Item name="sharepoint_site_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="sharepoint_drive_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="sharepoint_folder_item_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="sharepoint_folder_web_url" hidden>
              <Input />
            </Form.Item>
            <Form.Item shouldUpdate noStyle>
              {() => {
                const webUrl = (
                  editProjectForm.getFieldValue('sharepoint_folder_web_url') as string | undefined
                )?.trim();
                const resolved: ResolveSharePointSharingLinkResponseDTO | null = webUrl
                  ? {
                      sharepoint_site_id:
                        (editProjectForm.getFieldValue('sharepoint_site_id') as
                          string | undefined) ?? '',
                      sharepoint_drive_id:
                        (editProjectForm.getFieldValue('sharepoint_drive_id') as
                          string | undefined) ?? '',
                      sharepoint_folder_item_id:
                        (editProjectForm.getFieldValue('sharepoint_folder_item_id') as
                          string | undefined) ?? '',
                      sharepoint_folder_web_url: webUrl,
                    }
                  : null;

                return (
                  <SharePointLinkResolver
                    accessToken={accessToken}
                    microsoftOboAssertion={microsoftOboAssertion}
                    clientId={editingRow?.client_id ?? ''}
                    projectId={editingRow?.id ?? ''}
                    resolved={resolved}
                    onResolved={handleSharePointResolved}
                  />
                );
              }}
            </Form.Item>
          </Form>
        )}
      </Modal>
    </>
  );
};

export default ClientProjectsPage;
